import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Box, Button, Icon, PulsingOrb, Text } from '~/atomic';
import { useAppDispatch, useAppSelector } from '~/store';
import { selectLogin } from '~/store/auth/login/selectors';
import { verifyEmailToken } from '~/store/auth/login/thunks';

type Status = 'loading' | 'success' | 'error';

export default function VerifyEmailView() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [params] = useSearchParams();
  const { signed } = useAppSelector(selectLogin);
  const [status, setStatus] = useState<Status>('loading');
  const ran = useRef(false);

  const token = params.get('token');

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    if (!token) {
      setStatus('error');
      return;
    }

    (async () => {
      const ok = await dispatch(verifyEmailToken(token)).unwrap();
      setStatus(ok ? 'success' : 'error');
    })();
  }, [dispatch, token]);

  const goNext = () => {
    navigate(signed ? '/app/home' : '/auth/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-intense-01 flex flex-col items-center justify-center px-small">
      <Box className="w-full xl:max-w-[440px] flex flex-col items-center text-center gap-medium">
        {status === 'loading' && (
          <>
            <PulsingOrb size={120} />
            <Text className="text-headingH2 color-complement-01" as="h1">
              Confirmando seu email...
            </Text>
            <Text className="text-bodyMD color-intense-06">
              Estamos validando seu link de confirmação. Isso leva só um instante.
            </Text>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="h-[96px] w-[96px] rounded-full bg-success-01 flex items-center justify-center">
              <Icon name="icon-circle-check" size={48} color="#FFFFFF" rounded={false} />
            </div>
            <Text className="text-headingH1 color-complement-01" as="h1">
              Email confirmado!
            </Text>
            <Text className="text-bodyMD color-intense-06">
              Sua conta está liberada para cadastrar contratos e registrar
              pagamentos.
            </Text>
            <Button
              fullWidth
              text={signed ? 'Ir para o app' : 'Entrar'}
              rightIcon="icon-arrow-right"
              onClick={goNext}
            />
          </>
        )}

        {status === 'error' && (
          <>
            <div className="h-[96px] w-[96px] rounded-full bg-warning-01 flex items-center justify-center">
              <Icon name="icon-close" size={48} color="#992929" rounded={false} />
            </div>
            <Text className="text-headingH1 color-complement-01" as="h1">
              Link inválido ou expirado
            </Text>
            <Text className="text-bodyMD color-intense-06">
              Faça login na sua conta e solicite um novo email de confirmação.
            </Text>
            <Button
              fullWidth
              text="Ir para o login"
              rightIcon="icon-arrow-right"
              onClick={() => navigate('/auth/login', { replace: true })}
            />
          </>
        )}
      </Box>
    </div>
  );
}
